import styled from '@emotion/styled';
import React, { useState } from 'react';
import { Css, Responsive, SortingIcon, TextButton } from '@/ui';
import { ITableRow } from '../../../contentful/types/INonStopDestinationsTableRowEntry';
import {
	Section,
	SectionHeader,
} from '../../content-sections/ui/components/Section';
import { NonStopDestinationsTableRow } from './NonStopDestinationsTableRow';
import { getHeadingStyle } from '../../../styles/utils/getHeadingStyle';
import { theme } from '../../../styles/theme';
import { Color } from '../../../styles/Color';
import { INonStopDestinationsTableSectionEntry } from '../../../contentful/types/INonStopDestinationsTableSectionEntry';
import { FONT_FAMILY_AVENIR_NEXT } from '../../../styles/Font';

export interface ITableSectionsProps {
	section: INonStopDestinationsTableSectionEntry;
}

export function NonStopDestinationsTableSection({
	section,
}: ITableSectionsProps) {
	const { title, rows } = section.fields;

	const [sortedOn, setSortedOn] = useState<'destination' | 'airline'>(
		'destination',
	);

	const sortedRows = rows?.sort((a, b) => {
		if (sortedOn === 'destination') {
			return a.fields.destination.localeCompare(b.fields.destination);
		}
		return a.fields.airline.localeCompare(b.fields.airline);
	});

	const onClickSortTable = () => {
		setSortedOn((prev) => (prev === 'destination' ? 'airline' : 'destination'));
	};

	const label = sortedOn === 'destination' ? 'On Destination' : 'On Airline';

	const isTablet = !!Responsive.useMatchesBreakpoint('tablet');

	const tableHeaders = [
		{ key: 'airline', label: 'Airline(s)' },
		{ key: 'airportCode', label: 'Airport Code', className: 'airport-column' },
		{ key: 'destination', label: 'Destination' },
	];

	return (
		<Section>
			{title && <SectionHeader>{title}</SectionHeader>}
			{isTablet ? (
				<StyledSortTableButton type="button" onClick={onClickSortTable}>
					{label} <SortingIcon />
				</StyledSortTableButton>
			) : null}
			<StyledTable>
				<thead>
					<tr>
						{(sortedOn === 'airline'
							? tableHeaders
							: [...tableHeaders].reverse()
						).map((header) => (
							<th key={header.key} scope="col" className={header.className}>
								{header.label}
							</th>
						))}
						{!isTablet ? (
							<th className="th-sorting-button">
								<StyledSortTableButton type="button" onClick={onClickSortTable}>
									{label} <SortingIcon />
								</StyledSortTableButton>
							</th>
						) : null}
					</tr>
				</thead>
				<tbody>
					{sortedRows?.map((row: ITableRow) => {
						const { destination, airportCode, airline } = row.fields;

						return (
							<NonStopDestinationsTableRow
								key={row.id}
								destination={destination}
								airportCode={airportCode}
								airline={airline}
								sortedOn={sortedOn}
							/>
						);
					})}
				</tbody>
			</StyledTable>
		</Section>
	);
}

const StyledTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin: ${Css.pxToRem(32)} 0;

	thead {
		th {
			padding: 0;

			&.airport-column {
				padding: 0 20px;
			}

			@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
				&:nth-last-of-type(1) {
					text-align: right;
				}
			}
		}

		.th-sorting-button {
			display: flex;
			justify-content: end;
		}
	}

	th,
	td {
		padding: ${Css.pxToRem(20)} 0;
		text-align: left;
	}

	th {
		${getHeadingStyle(theme, 'h6')};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			font-size: 18px;
		}
	}

	td {
		border-bottom: 1px solid #d9d9d9;

		&.styled-letter-row {
			${getHeadingStyle(theme, 'h6')};
			color: ${Color.PrimaryBlue};
			padding-bottom: 0;
			padding-top: ${Css.pxToRem(32)};
			border-bottom: none;
		}

		&.airport-column {
			padding: 0 20px;
		}

		@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
			&:nth-last-of-type(2) {
				text-align: right;
			}
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		margin-top: 16px;
	}

	.airline-column {
		max-width: 170px;

		@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
			max-width: 270px;
		}
	}

	.airport-column {
		min-width: 180px;
	}
`;

const StyledSortTableButton = styled(TextButton)`
	display: flex;
	align-items: center;
	gap: ${Css.pxToRem(8)};
	height: 35px;
	justify-content: end;
	cursor: pointer;
	color: ${Color.PrimaryBlue};
	font-size: ${Css.pxToRem(16)};
	font-weight: 500;
	padding: 0;
	min-width: ${Css.pxToRem(140)};

	&:hover {
		color: ${Color.PrimaryBlue};
	}

	svg {
		path {
			fill: ${Color.PrimaryBlue};
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		justify-content: start;
		font-family: ${FONT_FAMILY_AVENIR_NEXT};
	}
`;
